import Nango from '@nangohq/frontend'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import { NangoConnection } from '@/domain/nango-connection'
import {
  checkNangoConnection,
  createNangoConnectSession,
  createNangoReconnectSession
} from './use-nango-connections'

// Serves both the Connect UI and the session-token API routes it calls.
export const NANGO_URL = 'https://nango.byrever.com'

// Connecting creates the credential for the group only: choosing its stores is a separate step.
export function useNangoConnect(
  group: string | undefined,
  onDone: () => void,
  allowedIntegrations?: string[]
) {
  const { t, i18n } = useTranslation()

  // `only` narrows the integrations for this one connect, e.g. the one picked in our own list.
  return async (only?: string[]) => {
    if (!group) return
    const connectUI = new Nango().openConnectUI({
      baseURL: NANGO_URL,
      apiURL: NANGO_URL,
      lang: i18n.language,
      onEvent: (event) => {
        if (event.type === 'connect' || event.type === 'close') onDone()
      }
    })
    try {
      const session = await createNangoConnectSession(
        group,
        only ?? allowedIntegrations
      )
      connectUI.setSessionToken(session.token)
    } catch {
      connectUI.close()
      toast({ text: t('nango_credentials.session_error'), variant: 'error' })
    }
  }
}

// Reconnecting keeps the connection's id. Once Nango holds the new credentials the store is asked again, so the
// warning clears without waiting for a store call to succeed.
export function useNangoReconnect(
  group: string | undefined,
  onDone: () => void
) {
  const { t, i18n } = useTranslation()

  return async (
    connection: Pick<NangoConnection, 'integration' | 'connectionId'>
  ) => {
    if (!group) return
    const connectUI = new Nango().openConnectUI({
      baseURL: NANGO_URL,
      apiURL: NANGO_URL,
      lang: i18n.language,
      onEvent: (event) => {
        if (event.type === 'connect') {
          // A store that can't answer leaves the old state: the list rechecks it later.
          checkNangoConnection(group, connection)
            .catch(() => undefined)
            .finally(onDone)
        } else if (event.type === 'close') {
          onDone()
        }
      }
    })
    try {
      const session = await createNangoReconnectSession(group, connection)
      connectUI.setSessionToken(session.token)
    } catch {
      connectUI.close()
      toast({
        text: t('integrations_hub.attention.reconnect_error'),
        variant: 'error'
      })
    }
  }
}
